import React from 'react';
import { Button } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { formatConnectionTime, isIcelandAirFlight } from '../../utils/connectionUtils';

const PrintDispatchButton = ({ runs, tdrData, brockData, disabled, hideColdGates = false }) => {
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert("Please allow popups for this website to use the print feature.");
      return;
    }

    const inboundInfo = tdrData?.inbound || {};
    const drivers = runs?.drivers || [];
    const activeDrivers = drivers.filter(d => d.flights.length > 0);
    
    const binNames = brockData?.bins ? Object.keys(brockData.bins).sort().reverse() : [];

    // Carousel assignments based on gate - Revised 9/4/2026
    const carouselAssignments = {
      // Sector 1
      23: { sector: 1, claim: 4 },
      24: { sector: 1, claim: 3 },
      25: { sector: 1, claim: 5 },
      26: { sector: 1, claim: 2 },
      27: { sector: 1, claim: 6 },
      28: { sector: 1, claim: 1 },
      29: { sector: 1, claim: 4 },
      31: { sector: 1, claim: 3 },
      33: { sector: 1, claim: 5 },
      35: { sector: 1, claim: 2 },
      37: { sector: 1, claim: 6 },
      39: { sector: 1, claim: 1 },
      // Sector 2
      40: { sector: 2, claim: 1 },
      41: { sector: 2, claim: 6 },
      42: { sector: 2, claim: 2 },
      43: { sector: 2, claim: 5 },
      44: { sector: 2, claim: 3 },
      45: { sector: 2, claim: 4 },
      46: { sector: 2, claim: 1 },
      47: { sector: 2, claim: 6 },
      48: { sector: 2, claim: 5 },
      49: { sector: 2, claim: 3 },
      50: { sector: 2, claim: 4 },
      51: { sector: 2, claim: 3 },
      52: { sector: 2, claim: 5 },
      53: { sector: 2, claim: 2 },
      54: { sector: 2, claim: 6 },
      55: { sector: 2, claim: 1 },
      // Sector 3
      56: { sector: 3, claim: 4 },
      57: { sector: 3, claim: 3 },
      58: { sector: 3, claim: 5 },
      59: { sector: 3, claim: 2 },
      60: { sector: 3, claim: 6 },
      61: { sector: 3, claim: 1 },
      62: { sector: 3, claim: 4 },
      63: { sector: 3, claim: 3 },
      64: { sector: 3, claim: 5 },
      65: { sector: 3, claim: 2 },
      66: { sector: 3, claim: 6 },
      67: { sector: 3, claim: 1 }
    };

    const getCarouselInfo = (gate) => {
      if (!gate) return { sector: '?', claim: '?' };
      // Extract just the numeric part of the gate (e.g., "C24" -> 24)
      const gateNum = parseInt(gate.replace(/[A-Z]/gi, ''), 10);
      return carouselAssignments[gateNum] || { sector: '?', claim: '?' };
    };

    // Calculate goal time (ETA + 22 minutes) in 24-hour format
    const calculateGoalTime = (etaString) => {
      if (!etaString) return '';
      const [time, period] = etaString.split(' ');
      const [hours, minutes] = time.split(':').map(num => parseInt(num));
      
      let totalMinutes = hours * 60 + minutes;
      if (period === 'PM' && hours !== 12) totalMinutes += 12 * 60;
      if (period === 'AM' && hours === 12) totalMinutes -= 12 * 60;
      
      totalMinutes += 22; // Add 22 minutes goal
      
      const goalHours = Math.floor(totalMinutes / 60) % 24;
      const goalMinutes = totalMinutes % 60;
      
      return `${goalHours.toString().padStart(2, '0')}:${goalMinutes.toString().padStart(2, '0')}`;
    };

    // Escape paste-derived/user-typed values before interpolating into the
    // print HTML (written via document.write, so React's escaping doesn't apply)
    const esc = (value) => {
      if (value === null || value === undefined) return '';
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    };

    // Returns HTML-safe text — only used inside the print HTML template
    const displayFlightNumber = (flightNum) => {
      if (!flightNum) return '';
      return esc(flightNum.replace(/^WN\s*/, ''));
    };

    const displayGate = (flight) => {
      // Cancelled flights always show "Reroutes"
      if (flight.status?.toLowerCase().includes('cancel')) {
        return 'Reroutes';
      }
      // Overnight freight shows "Stays at Gate"
      if ((flight.isFreightOnly || flight.connectionCategory === 'freight-only') && flight.isOvernight) {
        return 'Stays at Gate';
      }
      // Overnight bags (not freight) show "Reroutes"
      if (flight.isOvernight && !(flight.isFreightOnly || flight.connectionCategory === 'freight-only')) {
        return 'Reroutes';
      }
      // Iceland Air flights always show "Cold Sort"
      if (isIcelandAirFlight(flight.flightNumber || flight.originalFlightNumber)) {
        return 'Cold Sort';
      }
      if (hideColdGates && flight.connectionCategory === 'cold') {
        return 'Cold Sort';
      }
      return esc(flight.gate);
    };

    const displayConnectionTime = (flight) => {
      // Cancelled flights always show "Cancelled"
      if (flight.status?.toLowerCase().includes('cancel')) {
        return 'Cancelled';
      }
      // Iceland Air flights don't show connection time
      if (isIcelandAirFlight(flight.flightNumber || flight.originalFlightNumber)) {
        return '';
      }
      return esc(formatConnectionTime(flight.connectionTime));
    };

    const buildConcourseMapHTML = (activeGates, hotGates, inboundGateNum) => {
      const getGateStyle = (gateNum) => {
        if (gateNum === inboundGateNum) {
          return {
            bg: '#000',
            text: '#fff',
            border: '2px solid #000',
            fontWeight: 'bold'
          };
        }
        if (hotGates.includes(gateNum) || activeGates.includes(gateNum)) {
          return {
            bg: '#fff',
            text: '#000',
            border: '2px solid #000',
            fontWeight: 'bold'
          };
        }
        return {
          bg: '#fff',
          text: '#999',
          border: '1px solid #999',
          fontWeight: 'normal'
        };
      };

      const buildGateHTML = (gateNum, position) => {
        const style = getGateStyle(gateNum);
        return `
          <div style="position: absolute; left: ${position};">
            <div style="width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; 
                        background-color: ${style.bg} !important; color: ${style.text} !important; border: ${style.border}; 
                        border-radius: 3px; font-size: 9px; font-weight: ${style.fontWeight}; box-sizing: border-box;
                        -webkit-print-color-adjust: exact; print-color-adjust: exact;">
              ${gateNum}
            </div>
          </div>
        `;
      };

      return `
        <div style="background-color: #fff; padding: 10px; margin: 10px 0; border: 1px solid #666; page-break-inside: avoid;">
          <div style="position: relative; width: 100%; height: 120px;">
            <!-- North Side (Odd gates) -->
            <div style="position: absolute; top: 20px; left: 0; width: 100%; display: flex;">
              <!-- West wing -->
              <div style="width: 33%; position: relative;">
                ${buildGateHTML(23, '0%')}
                ${buildGateHTML(25, '10%')}
                ${buildGateHTML(27, '20%')}
                ${buildGateHTML(29, '30%')}
                ${buildGateHTML(31, '40%')}
                ${buildGateHTML(33, '57%')}
                ${buildGateHTML(35, '67.5%')}
                ${buildGateHTML(37, '78%')}
                ${buildGateHTML(39, '88.5%')}
              </div>
              <!-- East wing -->
              <div style="width: 67%; position: relative;">
                ${buildGateHTML(41, '2%')}
                ${buildGateHTML(43, '8%')}
                ${buildGateHTML(45, '14%')}
                ${buildGateHTML(47, '20%')}
                ${buildGateHTML(49, '29.7%')}
                ${buildGateHTML(51, '37.1%')}
                ${buildGateHTML(53, '44%')}
                ${buildGateHTML(55, '50.6%')}
                ${buildGateHTML(57, '60.3%')}
                ${buildGateHTML(59, '67.4%')}
                ${buildGateHTML(61, '74%')}
                ${buildGateHTML(63, '80.6%')}
                ${buildGateHTML(65, '90%')}
                ${buildGateHTML(67, '95%')}
              </div>
            </div>

            <!-- Concourse Lines -->
            <div style="position: absolute; top: 42px; left: 0; width: 32%; height: 2px; background-color: #666;"></div>
            <div style="position: absolute; top: 42px; right: 0; width: 66%; height: 2px; background-color: #666;"></div>
            <div style="position: absolute; top: 78px; left: 0; width: 32%; height: 2px; background-color: #666;"></div>
            <div style="position: absolute; top: 78px; right: 0; width: 66%; height: 2px; background-color: #666;"></div>

            <!-- South Side (Even gates) -->
            <div style="position: absolute; top: 80px; left: 0; width: 100%; display: flex;">
              <!-- West wing -->
              <div style="width: 33%; position: relative;">
                ${buildGateHTML(24, '0%')}
                ${buildGateHTML(26, '14%')}
                ${buildGateHTML(28, '27%')}
                ${buildGateHTML(30, '40%')}
                ${buildGateHTML(32, '57%')}
                ${buildGateHTML(34, '67.5%')}
                ${buildGateHTML(36, '78%')}
                ${buildGateHTML(38, '88.5%')}
              </div>
              <!-- East wing -->
              <div style="width: 67%; position: relative;">
                ${buildGateHTML(40, '2%')}
                ${buildGateHTML(42, '8%')}
                ${buildGateHTML(44, '14%')}
                ${buildGateHTML(46, '20%')}
                ${buildGateHTML(48, '29.7%')}
                ${buildGateHTML(50, '37.1%')}
                ${buildGateHTML(52, '44%')}
                ${buildGateHTML(54, '50.6%')}
                ${buildGateHTML(56, '60.3%')}
                ${buildGateHTML(58, '67.4%')}
                ${buildGateHTML(60, '74%')}
                ${buildGateHTML(62, '80.6%')}
                ${buildGateHTML(64, '90%')}
                ${buildGateHTML(66, '95%')}
              </div>
            </div>

            <!-- Tunnels -->
            <!-- West Tunnel -->
            <div style="position: absolute; left: calc(53.5% * 0.313); top: 42px; width: 14px; height: 36px; 
                        background-color: #666; transform: translateX(-7px);"></div>
            <div style="position: absolute; left: calc(53.5% * 0.313 - 35px); top: 50%; transform: translateY(-50%); 
                        font-size: 8px; font-weight: bold; color: #000; text-align: center; white-space: nowrap;">
              <div>West</div>
              <div>Tunnel</div>
            </div>
            
            <!-- East Tunnel -->
            <div style="position: absolute; left: calc(45.5% + 67% * 0.072); top: 42px; width: 14px; height: 36px; 
                        background-color: #666; transform: translateX(-7px);"></div>
            <div style="position: absolute; left: calc(45.5% + 67% * 0.072 - 35px); top: 50%; transform: translateY(-50%); 
                        font-size: 8px; font-weight: bold; color: #000; text-align: center; white-space: nowrap;">
              <div>East</div>
              <div>Tunnel</div>
            </div>
            
            <!-- Breezeways -->
            <!-- Breezeway between 54-56 -->
            <div style="position: absolute; left: calc(35% + 67% * 0.536); top: 42px; width: 10px; height: 36px; 
                        background-color: #666; transform: translateX(-5px);"></div>
            
            <!-- Breezeway between 62-64 -->
            <div style="position: absolute; left: calc(33.75% + 67% * 0.853); top: 42px; width: 10px; height: 36px; 
                        background-color: #666; transform: translateX(-5px);"></div>
          </div>

          <!-- Legend -->
          <div style="display: flex; gap: 15px; margin-top: 8px; font-size: 10px;">
            <div style="display: flex; align-items: center;">
              <div style="width: 12px; height: 12px; background-color: #000; margin-right: 4px; border-radius: 2px;"></div>
              <span>Inbound Gate</span>
            </div>
            <div style="display: flex; align-items: center;">
              <div style="width: 12px; height: 12px; background-color: #fff; border: 2px solid #000; margin-right: 4px; 
                           border-radius: 2px; box-sizing: border-box;"></div>
              <span>Assigned Stops</span>
            </div>
            <div style="display: flex; align-items: center;">
              <div style="width: 12px; height: 12px; background-color: #666; margin-right: 4px; border-radius: 2px;"></div>
              <span>Tunnel/Breezeway</span>
            </div>
          </div>

        </div>
      `;
    };

    const buildDriverSections = () => {
      let genericCounter = 1;
      let hotCounter = 1;
      let priorityCounter = 1;
      let freightCounter = 1;
      let coldCounter = 1;

      const inboundGateNum = parseInt(inboundInfo.arrivalGate?.replace(/\D/g, ''), 10);

      // Calculate driver counts for display
      const totalDrivers = activeDrivers.length;
      const localDrivers = activeDrivers.filter(d => d.name.toLowerCase().includes('local')).length;
      const transferDrivers = totalDrivers - localDrivers;

      // Check if any flights are split (FWD/AFT mode)
      const hasAnySplitFlights = activeDrivers.some(d =>
        d.flights.some(f => f.isSplit && f.splitType)
      );

      // Count FWD/AFT drivers if in split mode
      let transferFwdCount = 0;
      let transferAftCount = 0;
      let localFwdCount = 0;
      let localAftCount = 0;

      if (hasAnySplitFlights) {
        activeDrivers.forEach(d => {
          const isLocal = d.name.toLowerCase().includes('local');
          const hasFwd = d.flights.some(f => f.splitType === 'FWD');
          const hasAft = d.flights.some(f => f.splitType === 'AFT');

          if (isLocal) {
            if (hasFwd) localFwdCount++;
            if (hasAft) localAftCount++;
          } else {
            if (hasFwd) transferFwdCount++;
            if (hasAft) transferAftCount++;
          }
        });
      }

      return activeDrivers.slice().reverse().map(driver => {
        // If any flight has runOrder, sort by runOrder (preserves Magic Button / manual reorder)
        const hasRunOrder = driver.flights.some(f => f.runOrder);
        const sortedFlights = hasRunOrder
          ? [...driver.flights].sort((a, b) => (a.runOrder || 999) - (b.runOrder || 999))
          : [...driver.flights].sort((a, b) => {
              // Pinned flights come first
              if (a.pinPosition && !b.pinPosition) return -1;
              if (!a.pinPosition && b.pinPosition) return 1;

              // Among pinned flights, sort by pin position
              if (a.pinPosition && b.pinPosition) {
                return a.pinPosition - b.pinPosition;
              }

              // Among unpinned flights, sort by ETD
              return (a.etd || '').localeCompare(b.etd || '');
            });
        // Detect duplicate destinations for same-city warnings.
        // Count UNIQUE base flight numbers per dest so split tiles
        // (FWD/AFT, BAGS/FREIGHT) of the same flight don't get
        // counted twice and trigger a false warning.
        const destFlightSets = {};
        sortedFlights.forEach(f => {
          const dest = (f.destination || '').toUpperCase().trim();
          if (!dest) return;
          const baseFlightNumber = (f.flightNumber || '').replace(/-FWD|-AFT|-BAGS|-FREIGHT/g, '').trim();
          if (!baseFlightNumber) return;
          if (!destFlightSets[dest]) destFlightSets[dest] = new Set();
          destFlightSets[dest].add(baseFlightNumber);
        });
        const destCounts = Object.fromEntries(
          Object.entries(destFlightSets).map(([dest, set]) => [dest, set.size])
        );

        const bagFlights = sortedFlights.filter(f => {
          // Include flights with bags that are either:
          // 1. Not cold category, OR
          // 2. Are overnight bags (even if marked as cold, overnight bags should be in regular section)
          return (f.bags || 0) > 0 && (f.connectionCategory !== 'cold' || (f.isOvernight && !(f.isFreightOnly || f.connectionCategory === 'freight-only')));
        });
        const coldBagFlights = sortedFlights.filter(f => {
          // Include flights with bags that are cold category BUT NOT overnight bags
          return (f.bags || 0) > 0 && f.connectionCategory === 'cold' && !(f.isOvernight && !(f.isFreightOnly || f.connectionCategory === 'freight-only'));
        });
        const freightFlights = sortedFlights.filter(f => {
          // Include flights that have freight in freightByBin OR allFreight
          const hasFreightByBin = f.freightByBin && Object.values(f.freightByBin).some(count => count > 0);
          const hasAllFreight = f.allFreight && f.allFreight.length > 0;
          const hasDestinationFreight = f.destinations && f.destinations.some(dest => dest.freight && dest.freight.length > 0);
          return hasFreightByBin || hasAllFreight || hasDestinationFreight;
        });
        
        // Extract gates for this driver
        const driverGates = sortedFlights
          .map(flight => {
            if (flight.status?.toLowerCase().includes('cancel') || 
                isIcelandAirFlight(flight.flightNumber || flight.originalFlightNumber) ||
                (hideColdGates && flight.connectionCategory === 'cold')) {
              return null;
            }
            return parseInt(flight.gate?.replace(/\D/g, ''), 10);
          })
          .filter(gate => gate && !isNaN(gate));
        
        // Identify hot gates for this driver
        const driverHotGates = sortedFlights
          .filter(flight => flight.connectionCategory === 'hot')
          .map(flight => parseInt(flight.gate?.replace(/\D/g, ''), 10))
          .filter(gate => gate && !isNaN(gate));
        
        let driverDisplayName;
        const trimmedName = driver.name.trim();
        const isCurrentDriverLocal = driver.name.toLowerCase().includes('local');

        // Check if it's EXACTLY one of the default auto-generated names
        if (/^Local Driver\s*\d*$/i.test(trimmedName)) {
          // Keep local driver names as-is (Local Driver, Local Driver 1, etc.)
          driverDisplayName = driver.name;
        } else if (/^Driver\s*\d*$/i.test(trimmedName)) {
          // Only "Driver" or "Driver 1", "Driver 2", etc.
          driverDisplayName = `Driver ${genericCounter++}`;
          // Add "Solo" if this is the only transfer driver
          if (!isCurrentDriverLocal && transferDrivers === 1) {
            driverDisplayName += ' Solo';
          }
        } else if (/^Hot\s*\d*$/i.test(trimmedName) || trimmedName.toLowerCase() === 'hot') {
          // Only if it's exactly "Hot" or "Hot 1", etc.
          driverDisplayName = `Hot Bag Driver ${hotCounter++}`;
        } else if (/^Priority\s*\d*$/i.test(trimmedName) || trimmedName.toLowerCase() === 'priority') {
          // Only if it's exactly "Priority" or "Priority 1", etc.
          driverDisplayName = `Priority Driver ${priorityCounter++}`;
        } else if (/^Freight\s*\d*$/i.test(trimmedName) || trimmedName.toLowerCase() === 'freight') {
          // Only if it's exactly "Freight" or "Freight 1", etc.
          driverDisplayName = `Freight Driver ${freightCounter++}`;
        } else if (/^Cold\s*\d*$/i.test(trimmedName) || trimmedName.toLowerCase() === 'cold') {
          // Only if it's exactly "Cold" or "Cold 1", etc.
          driverDisplayName = `Cold Bag Driver ${coldCounter++}`;
        } else {
          // Any custom name like "SpringShot", "Hot Bag Runner", etc. stays exactly as typed
          driverDisplayName = driver.name;
        }

        const buildColdBagsTable = () => {
          if (coldBagFlights.length === 0) return '';
          return `
            <h4 class="cold-bags-header">Cold Bags</h4>
            <table>
              <thead>
                <tr>
                  <th>Flt #</th>
                  <th>Dest</th>
                  <th>Gate</th>
                  <th>STD</th>
                  <th>ETD</th>
                  <th>Connection</th>
                  <th>Total Bags</th>
                  ${binNames.map(bin => `<th>Bin ${esc(bin)}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${coldBagFlights.map(flight => {
                  // Add FIRST/SECOND/THIRD STOP to notes if flight is pinned
                  let flightNotes = flight.notes || '';
                  if (flight.pinPosition === 1) {
                    flightNotes = '**FIRST STOP** - ' + flightNotes;
                  } else if (flight.pinPosition === 2) {
                    flightNotes = '**SECOND STOP** - ' + flightNotes;
                  } else if (flight.pinPosition === 3) {
                    flightNotes = '**THIRD STOP** - ' + flightNotes;
                  }
                  // Add same-city warning for duplicate destinations
                  const dest = (flight.destination || '').toUpperCase().trim();
                  if (dest && destCounts[dest] > 1 && !flight.hasMultipleToSameDestination) {
                    // Board-marked dups already carry this warning in their editable
                    // tile note (which prints verbatim, including dispatcher edits or
                    // deletion). Auto-append only for dups that EMERGED after the
                    // board was built, e.g. a destination edited at the desk.
                    const warning = destCounts[dest] + ' ' + dest + ' flights - read tag numbers please';
                    flightNotes = flightNotes ? flightNotes + ' | ' + warning : warning;
                  }

                  const hasNotes = flightNotes && flightNotes.trim();
                  const isCancelled = flight.status?.toLowerCase().includes('cancel');
                  const isKEF = isIcelandAirFlight(flight.flightNumber || flight.originalFlightNumber);
                  const isThru = flight.isThruBag;
                  const isOvernightBag = flight.isOvernight && !(flight.isFreightOnly || flight.connectionCategory === 'freight-only');
                  const additionalClasses = isCancelled ? 'cancelled-row' : isOvernightBag ? 'overnight-row' : isKEF ? 'international-row' : isThru ? 'thru-row' : '';

                  // Multi-destination cold flight - show the onward tag cities under
                  // the primary, same format as the main run table, so the cold sort
                  // sees the cities the tags actually read
                  let coldBagsContent = esc(flight.bags || 0);
                  if (flight.isMultiDestination && flight.destinations && flight.destinations.length > 0) {
                    const primaryDest = flight.destinations[0];
                    const connectionsWithBags = flight.destinations.slice(1).filter(dest => (dest.bags || 0) > 0);
                    if (connectionsWithBags.length > 0) {
                      const primaryBagInfo = `${esc(primaryDest.city)}: ${esc(primaryDest.bags || 0)}`;
                      const connectionBagInfo = connectionsWithBags
                        .map(dest => `↳ ${esc(dest.city)}: ${esc(dest.bags)}`)
                        .join('<br>');
                      coldBagsContent = `
                        <div class="connections-container">
                          ${primaryBagInfo}<br>
                          ${connectionBagInfo}
                        </div>
                      `;
                    }
                  }
                  return `
                    <tr class="${isOvernightBag ? 'normal' : flight.connectionCategory}-row ${additionalClasses} ${hasNotes ? 'flight-with-notes' : ''}">
                      <td>${displayFlightNumber(flight.flightNumber)}</td>
                      <td>${esc(flight.destination)}</td>
                      <td>${displayGate(flight)}</td>
                      <td>${flight.sch ? esc(flight.sch.replace('L', '')) : '-'}</td>
                      <td>${flight.status?.toLowerCase().includes('cancel') ? 'Cancelled' : esc(flight.etd)}</td>
                      <td class="${flight.connectionTime < 0 ? 'negative-connection' : ''}">
                        ${displayConnectionTime(flight)}
                        ${flight.isOvernight ? ' (Next Day)' : ''}
                      </td>
                      <td style="vertical-align: top; line-height: 1.4;">${coldBagsContent}</td>
                      ${binNames.map(bin => `<td>${esc(flight.binDistribution?.[bin] || '-')}</td>`).join('')}
                    </tr>
                    ${hasNotes ? `
                      <tr class="flight-notes-row">
                        <td colspan="${7 + binNames.length}" class="flight-notes">
                          <strong>Note:</strong> ${esc(flightNotes)}
                        </td>
                      </tr>
                    ` : ''}
                  `;
                }).join('')}
              </tbody>
            </table>
          `;
        };

        const buildFreightTable = () => {
          if (freightFlights.length === 0) return '';
          return `
            <h4 class="freight-header">Freight</h4>
            <table>
              <thead>
                <tr>
                  <th>Flt #</th>
                  <th>Dest</th>
                  <th>Nose</th>
                  <th>Gate</th>
                  <th>${driver.name.toLowerCase().includes('local') ? 'ETA' : 'ETD'}</th>
                  <th>Connection</th>
                  <th>Freight</th>
                  ${binNames.map(bin => `<th>Bin ${esc(bin)}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${freightFlights.map(flight => {
                    const totalFreight = Object.values(flight.freightByBin || {}).reduce((a, b) => a + b, 0);
                    
                    // Format freight display - show individual items like FlightTile does
                    let freightDisplay = totalFreight.toString();
                    if (flight.allFreight && flight.allFreight.length > 0) {
                      freightDisplay = flight.allFreight.map(freight => {
                        const pieces = freight.pieces || freight.count || 0;
                        const weight = freight.weight || 0;
                        return `${esc(freight.type)} ${esc(pieces)}/${esc(weight)}`;
                      }).join(', ');
                    }
                    
                    // Add FIRST/SECOND/THIRD STOP to notes if flight is pinned
                    let flightNotes = flight.notes || '';
                    if (flight.pinPosition === 1) {
                      flightNotes = '**FIRST STOP** - ' + flightNotes;
                    } else if (flight.pinPosition === 2) {
                      flightNotes = '**SECOND STOP** - ' + flightNotes;
                    } else if (flight.pinPosition === 3) {
                      flightNotes = '**THIRD STOP** - ' + flightNotes;
                    }
                    // Add same-city warning for duplicate destinations
                    const dest = (flight.destination || '').toUpperCase().trim();
                    if (dest && destCounts[dest] > 1 && !flight.hasMultipleToSameDestination) {
                    // Board-marked dups already carry this warning in their editable
                    // tile note (which prints verbatim, including dispatcher edits or
                    // deletion). Auto-append only for dups that EMERGED after the
                    // board was built, e.g. a destination edited at the desk.
                      const warning = destCounts[dest] + ' ' + dest + ' flights - read tag numbers please';
                      flightNotes = flightNotes ? flightNotes + ' | ' + warning : warning;
                    }

                    if (flight.isMultiDestination && flight.destinations && flight.destinations.length > 0) {
                      // Multi-destination flight - show primary destination and connections in one row
                      const primaryDest = flight.destinations[0];
                      const connectionDests = flight.destinations.slice(1);
                      
                      // Build the freight column content with primary destination and connections listed vertically
                      const primaryFreightDisplay = primaryDest.freight && primaryDest.freight.length > 0
                        ? (primaryDest.freight.length === 1
                          ? `${esc(primaryDest.freight[0].type)} ${esc(primaryDest.freight[0].pieces || primaryDest.freight[0].count)}/${esc(primaryDest.freight[0].weight || 0)}`
                          : `${primaryDest.freight.length} Freight`)
                        : '-';

                      let freightContent = `${esc(primaryDest.city)}: ${primaryFreightDisplay}`;
                      if (connectionDests.length > 0) {
                        const connectionFreightInfo = connectionDests.map(dest => {
                          if (dest.freight && dest.freight.length > 0) {
                            const destFreightDisplay = dest.freight.length === 1
                              ? `${esc(dest.freight[0].type)} ${esc(dest.freight[0].pieces || dest.freight[0].count)}/${esc(dest.freight[0].weight || 0)}`
                              : `${dest.freight.length} Freight`;
                            return `↳ ${esc(dest.city)}: ${destFreightDisplay}`;
                          }
                          return `↳ ${esc(dest.city)}: -`;
                        }).join('<br>');
                        freightContent = `
                          <div class="connections-container">
                            ${freightContent}<br>
                            ${connectionFreightInfo}
                          </div>
                        `;
                      }

                      const hasNotes = flightNotes && flightNotes.trim();
                      const isCancelled = flight.status?.toLowerCase().includes('cancel');
                      const isThru = flight.isThruBag;
                      const additionalClasses = isCancelled ? 'cancelled-row' : isThru ? 'thru-row' : '';
                      return `
                        <tr class="freight-only-row ${additionalClasses} ${hasNotes ? 'flight-with-notes' : ''}">
                          <td>${displayFlightNumber(flight.flightNumber)}</td>
                          <td>${esc(primaryDest.city)}</td>
                          <td>${esc(flight.registration || '-')}</td>
                          <td>${displayGate(flight)}</td>
                          <td>${flight.status?.toLowerCase().includes('cancel') ? 'Cancelled' : esc(flight.etd)}</td>
                          <td class="${flight.connectionTime < 0 ? 'negative-connection' : ''}">
                            ${displayConnectionTime(flight)}
                            ${flight.isOvernight ? ' (Next Day)' : ''}
                          </td>
                          <td style="vertical-align: top; line-height: 1.4;">${freightContent}</td>
                          ${binNames.map(bin => `<td>${esc(flight.freightByBin?.[bin] || '-')}</td>`).join('')}
                        </tr>
                        ${hasNotes ? `
                          <tr class="flight-notes-row">
                            <td colspan="${7 + binNames.length}" class="flight-notes">
                              <strong>Note:</strong> ${esc(flightNotes)}
                            </td>
                          </tr>
                        ` : ''}
                      `;
                    } else {
                      // Single destination freight flight
                      const hasNotes = flightNotes && flightNotes.trim();
                      const isCancelled = flight.status?.toLowerCase().includes('cancel');
                      const isThru = flight.isThruBag;
                      const additionalClasses = isCancelled ? 'cancelled-row' : isThru ? 'thru-row' : '';
                      return `
                        <tr class="freight-only-row ${additionalClasses} ${hasNotes ? 'flight-with-notes' : ''}">
                          <td>${displayFlightNumber(flight.flightNumber)}</td>
                          <td>${esc(flight.destination)}</td>
                          <td>${esc(flight.registration || '-')}</td>
                          <td>${displayGate(flight)}</td>
                          <td>${flight.status?.toLowerCase().includes('cancel') ? 'Cancelled' : esc(flight.etd)}</td>
                          <td class="${flight.connectionTime < 0 ? 'negative-connection' : ''}">
                            ${displayConnectionTime(flight)}
                            ${flight.isOvernight ? ' (Next Day)' : ''}
                          </td>
                          <td>${freightDisplay /* components escaped above */}</td>
                          ${binNames.map(bin => `<td>${esc(flight.freightByBin?.[bin] || '-')}</td>`).join('')}
                        </tr>
                        ${hasNotes ? `
                          <tr class="flight-notes-row">
                            <td colspan="${7 + binNames.length}" class="flight-notes">
                              <strong>Note:</strong> ${esc(flightNotes)}
                            </td>
                          </tr>
                        ` : ''}
                      `;
                    }
                }).join('')}
              </tbody>
            </table>
          `;
        };
        
        return `
          <div class="driver-section">
            <!-- Inbound Flight Header for each page -->
            <div class="inbound-header-repeat">
              <h1>Inbound Flight: ${displayFlightNumber(inboundInfo.flightNumber)} from ${esc(inboundInfo.arrivingFrom)}</h1>
              <table class="inbound-table">
                <thead>
                  <tr>
                    <th>Flight Number</th>
                    <th>Arriving From</th>
                    <th>Registry</th>
                    <th>Arrival Gate</th>
                    <th>ETA</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>${displayFlightNumber(inboundInfo.flightNumber)}</td>
                    <td>${esc(inboundInfo.arrivingFrom)}</td>
                    <td>${esc(inboundInfo.registry)}</td>
                    <td>${esc(inboundInfo.arrivalGate)}</td>
                    <td>${esc(inboundInfo.eta)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div class="driver-header">
              <h2>${esc(driverDisplayName)}</h2>
              <div style="font-size: 13px; color: #555; padding-left: 15px; padding-bottom: 5px;">
                <span style="background-color: #FFFF99; padding: 2px 6px; -webkit-print-color-adjust: exact; print-color-adjust: exact;">
                  ${totalDrivers > 1 ? (() => {
                    // Special case: if this is a transfer driver and there's only 1 transfer driver total
                    if (!isCurrentDriverLocal && transferDrivers === 1 && localDrivers > 0) {
                      return `Working with ${localDrivers} local driver${localDrivers > 1 ? 's' : ''}`;
                    }

                    if (hasAnySplitFlights) {
                      // Show FWD/AFT breakdown
                      let parts = [];
                      if (transferDrivers > 0) {
                        let transferParts = [];
                        if (transferFwdCount > 0) transferParts.push(`${transferFwdCount} FWD`);
                        if (transferAftCount > 0) transferParts.push(`${transferAftCount} AFT`);
                        parts.push(`${transferDrivers} transfer (${transferParts.join(', ')})`);
                      }
                      if (localDrivers > 0) {
                        let localParts = [];
                        if (localFwdCount > 0) localParts.push(`${localFwdCount} FWD`);
                        if (localAftCount > 0) localParts.push(`${localAftCount} AFT`);
                        if (localParts.length > 0) {
                          parts.push(`${localDrivers} local (${localParts.join(', ')})`);
                        } else {
                          parts.push(`${localDrivers} local`);
                        }
                      }
                      return `Working with ${totalDrivers - 1} other driver${totalDrivers - 1 > 1 ? 's' : ''} (${parts.join(' | ')})`;
                    } else {
                      // Standard display without FWD/AFT
                      return `Working with ${totalDrivers - 1} other driver${totalDrivers - 1 > 1 ? 's' : ''} (${transferDrivers} transfer, ${localDrivers} local)`;
                    }
                  })() : 'Solo driver on this flight'}
                </span>
              </div>
              ${driver.notes ? `<div class="driver-notes">${esc(driver.notes)}</div>` : ''}
            </div>
            ${driverGates.length > 0 && !driver.name.toLowerCase().includes('local') ? buildConcourseMapHTML(driverGates, driverHotGates, inboundGateNum) : ''}
            ${driver.name.toLowerCase().includes('local') && bagFlights.length > 0 ? `
            <!-- Goal Time Cards for Each Flight -->
            <div style="margin-bottom: 20px;">
              ${bagFlights.map(flight => {
                const carouselInfo = getCarouselInfo(flight.gate);
                const goalTime = calculateGoalTime(flight.etd);
                return `
                  <div style="border: 2px solid #000; border-radius: 8px; padding: 15px; margin-bottom: 15px; background-color: #fff;">
                    <div style="display: flex; justify-content: space-between; align-items: center;">
                      <div>
                        <h4 style="margin: 0; font-size: 18px;">${esc(inboundInfo.arrivingFrom || 'UNK')} ${displayFlightNumber(inboundInfo.flightNumber)} - Gate ${esc(flight.gate)}</h4>
                        <p style="margin: 5px 0; color: #333;">ETA: ${esc(flight.etd)} | ${esc(flight.bags || 0)} bags</p>
                      </div>
                      <div style="display: flex; gap: 15px;">
                        <div style="text-align: center; border: 2px solid #000; padding: 10px 20px; border-radius: 6px; background-color: #fff;">
                          <div style="font-size: 12px; margin-bottom: 3px; font-weight: bold;">CAROUSEL</div>
                          <div style="font-size: 28px; font-weight: bold;">${carouselInfo.claim}</div>
                        </div>
                        <div style="text-align: center; border: 2px solid #000; padding: 10px 20px; border-radius: 6px; background-color: #fff;">
                          <div style="font-size: 12px; margin-bottom: 3px; font-weight: bold;">DROP TIME GOAL</div>
                          <div style="font-size: 20px; font-weight: bold;">${goalTime}</div>
                        </div>
                      </div>
                    </div>
                    ${flight.notes ? `<div style="margin-top: 10px; padding: 8px; background-color: #fff; border: 1px solid #666; border-radius: 4px; font-size: 13px;"><strong>Note:</strong> ${esc(flight.notes)}</div>` : ''}
                  </div>
                `;
              }).join('')}
            </div>
            
            <!-- Bin Distribution Table for Local Drivers -->
            ${bagFlights.length > 0 ? `
            <h3 style="font-size: 14px; margin: 15px 0 10px 0;">Bin Distribution</h3>
            <table style="margin-bottom: 20px; table-layout: fixed; width: 100%;">
              <thead>
                <tr>
                  <th style="width: 15%;">Total Bags</th>
                  ${binNames.map(bin => `<th style="width: ${85 / binNames.length}%;">Bin ${bin}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${bagFlights.map(flight => {
                  return `
                    <tr>
                      <td>${esc(flight.bags || 0)}</td>
                      ${binNames.map(bin => `<td>${esc(flight.binDistribution?.[bin] || '-')}</td>`).join('')}
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
            
            <!-- Overall Bin Summary for Cart Planning -->
            <h3 style="font-size: 14px; margin: 20px 0 10px 0; padding-top: 15px; border-top: 2px solid #333;">Overall Bin Summary (Local + Transfer)</h3>
            <table style="margin: 0 auto 20px auto; table-layout: fixed; width: 70%; border: 1px solid #000;">
              <thead>
                <tr style="background-color: #fff;">
                  <th style="width: 16%; padding: 4px; font-weight: bold; font-size: 12px;">Bin</th>
                  <th style="width: 28%; padding: 4px; font-size: 12px;">Local Bags</th>
                  <th style="width: 28%; padding: 4px; font-size: 12px;">Transfer Bags</th>
                  <th style="width: 28%; padding: 4px; font-size: 12px;">Transfer Freight</th>
                </tr>
              </thead>
              <tbody>
                ${(() => {
                  // Calculate total bins from all sources
                  const localBins = {};
                  const transferBins = {};
                  const transferFreightBins = {};

                  // Get local bins from current driver's flights
                  bagFlights.forEach(flight => {
                    if (flight.binDistribution) {
                      Object.entries(flight.binDistribution).forEach(([bin, count]) => {
                        localBins[bin] = (localBins[bin] || 0) + (count || 0);
                      });
                    }
                  });

                  // Get transfer bins and freight from all non-local drivers
                  activeDrivers.forEach(d => {
                    if (!d.name.toLowerCase().includes('local')) {
                      d.flights.forEach(flight => {
                        if (flight.binDistribution) {
                          Object.entries(flight.binDistribution).forEach(([bin, count]) => {
                            transferBins[bin] = (transferBins[bin] || 0) + (count || 0);
                          });
                        }
                        // Get transfer freight
                        if (!flight.isLocalFreight && flight.allFreight) {
                          flight.allFreight.forEach(freight => {
                            const pieces = freight.pieces || freight.count || 0;
                            const weight = freight.weight || 0;
                            const type = freight.type || '';
                            
                            if (flight.freightByBin) {
                              Object.entries(flight.freightByBin).forEach(([bin, count]) => {
                                if (count > 0) {
                                  if (!transferFreightBins[bin]) {
                                    transferFreightBins[bin] = [];
                                  }
                                  // Proportionally distribute pieces and weight
                                  const totalFreightCount = Object.values(flight.freightByBin).reduce((a, b) => a + b, 0);
                                  const proportion = count / totalFreightCount;
                                  
                                  // Find existing entry for this type or create new one
                                  let existingEntry = transferFreightBins[bin].find(entry => entry.type === type);
                                  if (existingEntry) {
                                    existingEntry.pieces += Math.round(pieces * proportion);
                                    existingEntry.weight += Math.round(weight * proportion);
                                  } else {
                                    transferFreightBins[bin].push({
                                      type: type,
                                      pieces: Math.round(pieces * proportion),
                                      weight: Math.round(weight * proportion)
                                    });
                                  }
                                }
                              });
                            }
                          });
                        }
                      });
                    }
                  });
                  
                  // Generate rows for each bin - sort A to F
                  return binNames.slice().sort().map(bin => {
                    const localCount = localBins[bin] || 0;
                    const transferCount = transferBins[bin] || 0;
                    const transferFreightArray = transferFreightBins[bin];

                    // Format transfer freight with type (e.g., "NFG 3/150")
                    let transferFreightDisplay = '-';
                    if (transferFreightArray && transferFreightArray.length > 0) {
                      transferFreightDisplay = transferFreightArray
                        .map(f => `${esc(f.type)} ${f.pieces}/${f.weight}`)
                        .join(', ');
                    }

                    return `
                      <tr>
                        <td style="font-weight: bold; text-align: center; padding: 3px; font-size: 12px;">Bin ${bin}</td>
                        <td style="text-align: center; padding: 3px; font-size: 12px;">${localCount || '-'}</td>
                        <td style="text-align: center; padding: 3px; font-size: 12px;">${transferCount || '-'}</td>
                        <td style="text-align: center; padding: 3px; font-size: 11px;">${transferFreightDisplay}</td>
                      </tr>
                    `;
                  }).join('');
                })()}
              </tbody>
            </table>
            <p style="font-size: 16px; font-style: italic; text-align: center; margin: 0 0 15px 0;">Verify freight on inbound summary.</p>
            ` : ''}
            ` : ''}
            
            ${bagFlights.length > 0 && !driver.name.toLowerCase().includes('local') ? `
            <table>
              <thead>
                <tr>
                  <th>Flt #</th>
                  <th>Dest</th>
                  <th>Nose</th>
                  <th>Gate</th>
                  <th>ETD</th>
                  <th>Connection</th>
                  <th>Total Bags</th>
                  ${binNames.map(bin => `<th>Bin ${esc(bin)}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${bagFlights.map(flight => {
                  // Add FIRST/SECOND/THIRD STOP to notes if flight is pinned
                  let flightNotes = flight.notes || '';
                  if (flight.pinPosition === 1) {
                    flightNotes = '**FIRST STOP** - ' + flightNotes;
                  } else if (flight.pinPosition === 2) {
                    flightNotes = '**SECOND STOP** - ' + flightNotes;
                  } else if (flight.pinPosition === 3) {
                    flightNotes = '**THIRD STOP** - ' + flightNotes;
                  }
                  // Add same-city warning for duplicate destinations
                  const dest = (flight.destination || '').toUpperCase().trim();
                  if (dest && destCounts[dest] > 1 && !flight.hasMultipleToSameDestination) {
                    // Board-marked dups already carry this warning in their editable
                    // tile note (which prints verbatim, including dispatcher edits or
                    // deletion). Auto-append only for dups that EMERGED after the
                    // board was built, e.g. a destination edited at the desk.
                    const warning = destCounts[dest] + ' ' + dest + ' flights - read tag numbers please';
                    flightNotes = flightNotes ? flightNotes + ' | ' + warning : warning;
                  }
                  
                  // Notes are now handled in the FlightTile component
                  
                  if (flight.isMultiDestination && flight.destinations && flight.destinations.length > 0) {
                    // Multi-destination flight - show primary destination and connections in one row
                    const primaryDest = flight.destinations[0];
                    const connectionDests = flight.destinations.slice(1);
                    
                    // Build the bags column content with connections listed vertically - only show destinations with bags > 0
                    const connectionsWithBags = connectionDests.filter(dest => (dest.bags || 0) > 0);
                    
                    let bagsContent;
                    if (connectionsWithBags.length > 0) {
                      // Multi-destination with connections that have bags - show detailed format
                      let primaryBagInfo = `${esc(primaryDest.city)}: ${esc(primaryDest.bags || 0)}`;
                      let connectionBagInfo = connectionsWithBags
                        .map(dest => `↳ ${esc(dest.city)}: ${esc(dest.bags)}`)
                        .join('<br>');
                      
                      bagsContent = `
                        <div class="connections-container">
                          ${primaryBagInfo}<br>
                          ${connectionBagInfo}
                        </div>
                      `;
                    } else {
                      // Only primary destination has bags - show simple format like single destination
                      bagsContent = primaryDest.bags || 0;
                    }

                    const hasNotes = flightNotes && flightNotes.trim();
                    const isCancelled = flight.status?.toLowerCase().includes('cancel');
                    const isKEF = isIcelandAirFlight(flight.flightNumber || flight.originalFlightNumber);
                    const isThru = flight.isThruBag;
                    const isOvernightBag = flight.isOvernight && !(flight.isFreightOnly || flight.connectionCategory === 'freight-only');
                    const additionalClasses = isCancelled ? 'cancelled-row' : isOvernightBag ? 'overnight-row' : isKEF ? 'international-row' : isThru ? 'thru-row' : '';
                    return `
                      <tr class="${isOvernightBag ? 'normal' : flight.connectionCategory}-row ${additionalClasses} ${hasNotes ? 'flight-with-notes' : ''}">
                        <td>${displayFlightNumber(flight.flightNumber)}</td>
                        <td>${esc(primaryDest.city)}</td>
                        <td>${esc(flight.registration || '-')}</td>
                        <td>${displayGate(flight)}</td>
                        <td>${flight.status?.toLowerCase().includes('cancel') ? 'Cancelled' : esc(flight.etd)}</td>
                        <td class="${flight.connectionTime < 0 ? 'negative-connection' : ''}">
                          ${displayConnectionTime(flight)}
                          ${flight.isOvernight ? ' (Next Day)' : ''}
                        </td>
                        <td style="vertical-align: top; line-height: 1.4;">${bagsContent}</td>
                        ${binNames.map(bin => `<td>${esc(flight.binDistribution?.[bin] || '-')}</td>`).join('')}
                      </tr>
                      ${hasNotes ? `
                        <tr class="flight-notes-row">
                          <td colspan="${7 + binNames.length}" class="flight-notes">
                            <strong>Note:</strong> ${esc(flightNotes)}
                          </td>
                        </tr>
                      ` : ''}
                    `;
                  } else {
                    // Single destination flight
                    const hasNotes = flightNotes && flightNotes.trim();
                    const isCancelled = flight.status?.toLowerCase().includes('cancel');
                    const isKEF = isIcelandAirFlight(flight.flightNumber || flight.originalFlightNumber);
                    const isThru = flight.isThruBag;
                    const isOvernightBag = flight.isOvernight && !(flight.isFreightOnly || flight.connectionCategory === 'freight-only');
                    const additionalClasses = isCancelled ? 'cancelled-row' : isOvernightBag ? 'overnight-row' : isKEF ? 'international-row' : isThru ? 'thru-row' : '';
                    return `
                      <tr class="${isOvernightBag ? 'normal' : flight.connectionCategory}-row ${additionalClasses} ${hasNotes ? 'flight-with-notes' : ''}">
                        <td>${displayFlightNumber(flight.flightNumber)}</td>
                        <td>${flight.isLocal ? '' : esc(flight.destination)}</td>
                        <td>${esc(flight.registration || '-')}</td>
                        <td>${displayGate(flight)}</td>
                        <td>${flight.isLocal ? esc(flight.etd) : (flight.status?.toLowerCase().includes('cancel') ? 'Cancelled' : esc(flight.etd))}</td>
                        <td class="${flight.connectionTime < 0 ? 'negative-connection' : ''}">
                          ${flight.isLocal ? '' : displayConnectionTime(flight)}
                          ${!flight.isLocal && flight.isOvernight ? ' (Next Day)' : ''}
                        </td>
                        <td>${esc(flight.bags || 0)}</td>
                        ${binNames.map(bin => `<td>${esc(flight.binDistribution?.[bin] || '-')}</td>`).join('')}
                      </tr>
                      ${hasNotes ? `
                        <tr class="flight-notes-row">
                          <td colspan="${7 + binNames.length}" class="flight-notes">
                            <strong>Note:</strong> ${esc(flightNotes)}
                          </td>
                        </tr>
                      ` : ''}
                    `;
                  }
                }).join('')}
              </tbody>
            </table>
            ` : ''}
            
            ${buildColdBagsTable()}
            
            ${!driver.name.toLowerCase().includes('local') ? buildFreightTable() : ''}
          </div>
        `;
      }).join('');
    };

    const printDateTime = new Date().toLocaleString();

    const printHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Dispatch for Flight ${displayFlightNumber(inboundInfo.flightNumber)}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap');
            
            /* Force print colors */
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            @page {
              /* top right bottom left — left/right bumped to 0.3in so
                 content stays inside the printer's non-printable zone
                 (most consumer/office printers clip the outer 0.25in). */
              margin: 0.2in 0.3in 0.6in 0.3in;
              @bottom-center {
                content: "Printed on: ${printDateTime}";
                font-size: 12px;
                color: #666;
              }
            }
            body { font-family: 'Roboto', Arial, sans-serif; color: black; background: white; margin: 0; }
            h1, h2, h3 { margin: 0; padding: 0; text-align: center; }
            .page-header { text-align: center; margin-bottom: 20px; }
            .page-header h1 { font-size: 24px; margin-bottom: 5px; }
            .page-header h3 { font-size: 16px; font-weight: normal; margin-bottom: 15px; }
            .inbound-table { width: 80%; margin: 0 auto 20px auto; border-collapse: collapse; }
            
            .driver-section { 
              page-break-before: always; /* Start each driver on a new page */
              page-break-inside: avoid; /* Keep each driver run together on one page */
              margin-bottom: 40px;
              border: 2px solid #333;
              border-radius: 8px;
              padding: 15px;
              overflow: hidden;
            }
            .driver-section:first-child {
              page-break-before: auto; /* Don't add page break before the first driver */
            }
            
            /* Style the repeated header on each page */
            .inbound-header-repeat {
              text-align: center;
              margin-bottom: 20px;
            }
            .inbound-header-repeat h1 {
              font-size: 24px;
              margin-bottom: 5px;
            }
            .inbound-header-repeat table {
              width: 80%;
              margin: 0 auto 20px auto;
              border-collapse: collapse;
            }
            .driver-header {
              background-color: #fff;
              padding: 6px 10px;
              margin: -15px -15px 8px -15px;
              border-bottom: 2px solid #333;
            }
            .driver-header h2 { font-size: 16px; text-align: left; padding-left: 15px; margin: 0 0 2px 0; }
            .driver-notes {
              font-size: 12px;
              color: #666;
              padding-left: 12px;
              padding-right: 12px;
              padding-bottom: 2px;
              font-style: italic;
              line-height: 1.25;
              background-color: #FFFF99;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              display: inline-block;
              margin-left: 15px;
            }
            
            .bag-header, .cold-bags-header, .freight-header { text-align: left; font-size: 14px; margin: 10px 0 5px 0; }
            table { width: 100%; border-collapse: collapse; }
            thead { display: table-header-group; }
            
            /* --- Ensure tables break cleanly BETWEEN rows. Use both the
                   legacy and modern properties for cross-browser support. --- */
            tr, td { page-break-inside: avoid !important; break-inside: avoid !important; }

            table { width: 100%; table-layout: fixed; font-size: 11px; }
            th, td { border: 1px solid #666; padding: 6px; font-size: 11px; vertical-align: middle; }
            /* Keep "Cold Sort" / "Reroutes" on a single line so the row
               doesn't grow too tall to fit on a page. */
            td:nth-child(4) { white-space: nowrap; }
            th { text-align: center; }
            td { text-align: center; }
            th { background-color: #fff; font-weight: bold; border: 1px solid #666 !important; }
            tbody tr:nth-child(odd) { background-color: #fff; }
            td[rowspan] { vertical-align: middle; }
            
            /* Specific column widths to optimize space */
            th:nth-child(1), td:nth-child(1) { width: 6%; }  /* Flt # */
            th:nth-child(2), td:nth-child(2) { width: 6%; }  /* Dest */
            th:nth-child(3), td:nth-child(3) { width: 9%; }  /* Nose */
            th:nth-child(4), td:nth-child(4) { width: 10%; } /* Gate (wide enough for "Cold Sort") */
            th:nth-child(5), td:nth-child(5) { width: 6%; }  /* ETD */
            th:nth-child(6), td:nth-child(6) { width: 11%; padding: 3px; font-size: 10px; } /* Connection - smaller padding/font */
            th:nth-child(7), td:nth-child(7) { width: 10%; } /* Total Bags */
            /* Remaining columns (bins) share the rest of the space */
            
            .connections-container {
              display: inline-block;
              text-align: left;
            }

            .priority-row { background-color: rgba(249, 182, 18, 0.2) !important; }
            .hot-row { background-color: rgba(255, 0, 0, 0.1) !important; }
            .cold-row { background-color: rgba(0, 0, 255, 0.1) !important; }
            .international-row { background-color: rgba(128, 0, 128, 0.2) !important; }
            .freight-only-row { background-color: rgba(246, 83, 15, 0.2) !important; }
            .thru-row { background-color: rgba(76, 175, 80, 0.2) !important; }
            .cancelled-row { background-color: rgba(255, 20, 147, 0.3) !important; }
            .overnight-row { color: #9C27B0 !important; font-weight: bold; }
            .negative-connection { color: #D8000C; font-weight: bold; }
            
            /* Flight notes styling */
            .flight-with-notes { 
              border-top: 3px solid #333 !important;
              border-left: 3px solid #333 !important;
              border-right: 3px solid #333 !important;
              border-bottom: none !important;
            }
            .flight-notes-row { 
              border-left: 3px solid #333 !important;
              border-right: 3px solid #333 !important;
              border-bottom: 3px solid #333 !important;
              border-top: none !important;
            }
            .flight-notes-row td {
              background-color: #fff !important;
              font-style: italic;
              text-align: left !important;
              padding: 6px 12px !important;
              border-top: none !important;
              border-left: none !important;
              border-right: none !important;
              max-width: 300px;
              word-wrap: break-word;
            }
            .flight-notes { 
              font-size: 10px; 
              color: #333; 
            }
          </style>
        </head>
        <body>
          ${buildDriverSections()}
          <script>
            window.onload = function() {
              setTimeout(() => window.print(), 500);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(printHtml);
    printWindow.document.close();
  };

  return (
    <Button
      variant="contained"
      startIcon={<PrintIcon />}
      onClick={handlePrint}
      disabled={disabled}
      sx={{
        backgroundColor: '#6c757d',
        color: 'white',
        '&:hover': {
          backgroundColor: '#5a6268',
        },
        '&.Mui-disabled': {
          backgroundColor: 'rgba(255, 255, 255, 0.12)',
        },
      }}
    >
      Print Runs
    </Button>
  );
};

export default PrintDispatchButton;